import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { DevToolbar } from "@/components/DevToolbar";
import { LanguageSwitcher } from "./LanguageSwitcher";

interface NavItem {
  label: string;
  href: string;
}

interface NavbarProps {
  logo?: React.ReactNode;
  companyName?: string;
  navItems?: NavItem[];
  ctaText?: string;
}

/**
 * Navbar
 *
 * Responsive navigation with:
 * - Background on scroll
 * - Mobile hamburger menu with animated dropdown
 * - Smooth scroll to sections
 * - i18n-powered labels
 */
export function Navbar({ logo, companyName, navItems, ctaText }: NavbarProps) {
  const { t } = useTranslation("common");
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const resolvedCompanyName = companyName || t("org.name");
  const resolvedCtaText = ctaText || t("nav.getStarted");

  const resolvedNavItems: NavItem[] = navItems || [
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.pricing"), href: "#pricing" },
    { label: t("nav.testimonials"), href: "#testimonials" },
    { label: t("nav.faq"), href: "#faq" },
  ];

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={cn(
        "fixed top-0 right-0 left-0 z-50",
        "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
        isScrolled ? "border-b border-border bg-background/95 backdrop-blur-sm" : "bg-transparent",
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between lg:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 text-lg font-semibold text-foreground">
            {logo || (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-sm font-bold text-primary-foreground">A</span>
              </div>
            )}
            <span className="hidden sm:block">{resolvedCompanyName}</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-1 md:flex">
            {resolvedNavItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  "rounded-lg px-4 py-2 text-sm font-medium",
                  "text-muted-foreground hover:text-foreground",
                  "hover:bg-muted",
                  "transition-colors duration-200",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Auth & CTA Buttons */}
          <div className="hidden items-center gap-3 md:flex">
            <LanguageSwitcher />
            <DevToolbar />
            <Link
              to="/login"
              className={cn(
                "rounded-lg px-4 py-2 text-sm font-medium",
                "text-muted-foreground hover:text-foreground",
                "hover:bg-muted",
                "transition-colors duration-200",
              )}
            >
              {t("nav.signIn")}
            </Link>
            <Link to="/signup">
              <Button>{resolvedCtaText}</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              "rounded-lg p-2 md:hidden",
              "text-muted-foreground hover:text-foreground",
              "hover:bg-muted",
              "transition-colors duration-200",
            )}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "overflow-hidden md:hidden",
          "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          isMobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="border-t border-border bg-background">
          <div className="container mx-auto space-y-2 px-4 py-4">
            {resolvedNavItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  "block w-full rounded-lg px-4 py-3 text-left",
                  "text-muted-foreground hover:text-foreground",
                  "hover:bg-muted",
                  "transition-colors duration-200",
                )}
              >
                {item.label}
              </button>
            ))}
            <div className="flex items-center gap-3 px-4 py-2">
              <LanguageSwitcher />
              <DevToolbar />
            </div>
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                "block w-full rounded-lg px-4 py-3 text-left",
                "text-muted-foreground hover:text-foreground",
                "hover:bg-muted",
                "transition-colors duration-200",
              )}
            >
              {t("nav.signIn")}
            </Link>
            <div className="pt-2">
              <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full">{resolvedCtaText}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
